import { useEffect, useState, type ReactNode } from 'react'
import { CheckCheck, Clock3, ShoppingBag, Wallet } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { getBalance } from '../../services/balance'
import { getOrders } from '../../services/order'
import type { Balance } from '../../types'
import { count, money } from './format'
import DashboardTrends from './DashboardTrends'

type Resource<T> = { data: T | null; loading: boolean; error: string | null }

function useResource<T>(fetcher: () => Promise<T>) {
  const [state, setState] = useState<Resource<T>>({ data: null, loading: true, error: null })
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let cancelled = false
    setState({ data: null, loading: true, error: null })
    fetcher().then((data) => {
      if (!cancelled) setState({ data, loading: false, error: null })
    }).catch(() => {
      if (!cancelled) setState({ data: null, loading: false, error: '暫時無法載入' })
    })
    return () => { cancelled = true }
  }, [fetcher, retry])
  return { ...state, retry: () => setRetry((value) => value + 1) }
}

function todayParams() {
  // Top cards deliberately retain the browser-local creation-day definition.
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return { page: 1, size: 1, startDate: start.getTime(), endDate: end.getTime() - 1 }
}
const fetchBalance = async (): Promise<Balance> => (await getBalance()).data
const fetchToday = async () => (await getOrders(todayParams())).data.pagination.total
const fetchSuccessful = async () => {
  const params = todayParams()
  const [paid, settled] = await Promise.all([getOrders({ ...params, status: 'PAID' }), getOrders({ ...params, status: 'SETTLED' })])
  return paid.data.pagination.total + settled.data.pagination.total
}

function StatCard({ title, icon, value, unit, resource }: {
  title: string; icon: ReactNode; value: string; unit: string
  resource: { loading: boolean; error: string | null; retry: () => void }
}) {
  return <Card aria-label={title} className="min-w-0">
    <CardContent className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">{title}</span>
        <span className="flex size-9 items-center justify-center rounded-lg bg-secondary text-primary" aria-hidden="true">{icon}</span>
      </div>
      {resource.loading ? <div role="status" aria-label={`正在載入${title}`} className="h-8 w-28 animate-pulse rounded bg-muted" />
        : resource.error ? <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive"><span>{resource.error}</span><Button variant="outline" size="sm" aria-label={`重試${title}`} onClick={resource.retry}>重試</Button></div>
          : <div className="flex flex-wrap items-baseline gap-2"><strong className="break-all text-2xl font-semibold tracking-tight tabular-nums">{value}</strong><span className="text-xs text-muted-foreground">{unit}</span></div>}
    </CardContent>
  </Card>
}

export default function Dashboard() {
  const balance = useResource(fetchBalance)
  const today = useResource(fetchToday)
  const successful = useResource(fetchSuccessful)
  return <div className="dashboard-page space-y-6">
    <div><h1 className="text-2xl font-semibold tracking-tight">儀表盤</h1><p className="mt-1 text-sm text-muted-foreground">查看資金概況與每日交易表現</p></div>
    <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard title="可用餘額" icon={<Wallet size={18} />} value={money(balance.data?.available ?? '0')} unit="USD" resource={balance} />
      <StatCard title="待結算" icon={<Clock3 size={18} />} value={money(balance.data?.pending ?? '0')} unit="USD" resource={balance} />
      <StatCard title="今日訂單" icon={<ShoppingBag size={18} />} value={count(today.data ?? 0)} unit="筆" resource={today} />
      <StatCard title="今日成功訂單" icon={<CheckCheck size={18} />} value={count(successful.data ?? 0)} unit="筆" resource={successful} />
    </div>
    <DashboardTrends />
  </div>
}
